// src/pages/About.jsx
import React, { useState, useMemo, useRef } from "react";
import SEO from "../components/SEO";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  MapPin,
  Instagram,
  Facebook,
  ExternalLink,
  Clock,
  Award,
  ChefHat,
  Flame,
  Trophy,
  Send,
  ArrowRight,
  Star,
  Quote,
  Sparkles,
  BadgeCheck,
  Phone,
  Mail,
  Users,
  Heart,
  Check,
  UsersIcon,
  Utensils,
  GraduationCap,
  Medal, // Added for Special Guests
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* -----------------------------
   CONSTANTS & CONTENT (D10)
------------------------------ */
const D10 = {
  website: "https://www.d10s.pizza",
  menu: "/menu",
  instagram: "https://www.instagram.com/d10spizza/",
  facebook: "https://www.facebook.com/d10spizza/",
  addressLine: "Prosika 51A, 23250 Pag, Croatia",
  phoneDisplay: "+385 95 501 1065",
  phoneE164: "385955011065",
  email: "d10spizzafantasista@gmail.com",
  googleMaps: "https://www.google.com/maps/search/?api=1&query=Prosika%2051A%2C%2023250%20Pag%2C%20Croatia",
};

const ABOUT_STATS = [
  { value: "Prosika", label: "Home Base", icon: MapPin },
  { value: "Beach", label: "Front Row Seats", icon: Star },
  { value: "Late", label: "Still Hungry Hours", icon: Clock },
  { value: "#10", label: "Fantasista Energy", icon: Trophy },
];

const INGREDIENTS = [
  {
    id: "10",
    name: "Beach Rhythm",
    position: "The Foundation",
    origin: "Prosika, Pag",
    story:
      "This place runs on a simple rhythm: salty air, sunburned afternoons, and that slow shift into loud tables at night. You don’t need a plan. You just arrive, pick a spot, and suddenly you’re part of the scene. The vibe is the thing that holds everything together.",
    signature: "Easy pace, real summer energy",
    img: "/img/beach.jpeg",
  },
  {
    id: "8",
    name: "Island Character",
    position: "Home Advantage",
    origin: "Pag Island, Croatia",
    story:
      "Pag has an edge. Wind, salt, stone, and that no-nonsense island attitude. People here don’t over-explain things. They just show up and make it happen. D10’s is built the same way: straightforward, welcoming, and a little wild in the best possible way.",
    signature: "Salt, stone, and confidence",
    img: "/img/island.jpeg",
  },
  {
    id: "9",
    name: "The Table Culture",
    position: "The Link-Up",
    origin: "Any group, any night",
    story:
      "The best nights aren’t quiet. They’re shared. A table that starts as a quick stop becomes an hour, then two. Someone brings friends. Someone stays for one more. D10’s is designed for that exact moment when strangers become neighbours and the night becomes a memory.",
    signature: "Loud tables, warm welcome",
    img: "/img/table.jpeg",
  },
];

const TEAM = [
  {
    name: "Ivan Šetka",
    role: "Co-founder / Captain",
    origin: "Croatia",
    img: "/img/team/ivan.png",
  },
  {
    name: "Martina Bušić",
    role: "Co-founder / The Calm",
    origin: "Croatia",
    img: "/img/team/martina.png",
  },
  {
    name: "Maciej Gadomski",
    role: "Business talk only. No questions about age.",
    origin: "Poland",
    img: "/img/team/maciej.png",
  },
  {
    name: "Clevin Marquez",
    role: "Waiter on paper / jack of all trades in reality",
    origin: "Philippines",
    img: "/img/team/clevin.png",
  },
  {
    name: "Jan Filipczak",
    role: "Dough master / vanishing artist",
    origin: "Poland",
    img: "/img/team/jan.png",
  },
  {
    name: "Wojciech Filipczak",
    role: "Silent assassin / adopted son energy",
    origin: "Poland",
    img: "/img/team/wojtek.png",
  },
  {
    name: "Dora Kutija",
    role: "Always shows up when needed — bar, kitchen or reservations",
    origin: "Croatia",
    img: "/img/team/dora.png",
  },
  {
    name: "Karla Kutija",
    role: "Quality control — not afraid to send pizza back, even to instructors",
    origin: "Croatia",
    img: "/img/team/karla.png",
  },
  {
    name: "Tomislav Šetka",
    role: "Supposed Liverpool fan, always in a different jersey",
    origin: "Croatia",
    img: "/img/team/tomislav.png",
  },
  {
    name: "Jasna Radulović",
    role: "Unofficial Pag (especially Prosika) guide for first-timers",
    origin: "Serbia",
    img: "/img/team/jaca.png",
  },
];

const GUESTS = [
  {
    name: "Jernej Berlot",
    role: "Shows up when it matters, solves problems with flawless pizzas",
    origin: "Slovenia",
    img: "/img/team/jernej.png",
  },
  {
    name: "Pizza Shango",
    role: "Always welcome, same obsession for pizza life",
    origin: "Croatia",
    img: "/img/team/pizza-shango.png",
  },
  
  {
    name: "Magda Gadomska",
    role: "Makes it work whether it’s Paška janjetina or Polish pierogi",
    origin: "Poland",
    img: "/img/team/magda.png",
  },
  
];


const PLAYBOOK = [
  {
    icon: Trophy,
    title: "Good Energy Wins",
    text:
      "D10 is built for that feeling when the place is buzzing but still easy. You walk in, you feel welcome, you sit down, and the night starts doing its thing.",
  },
  {
    icon: Star,
    title: "Simple, On Purpose",
    text:
      "We don’t do “everything.” We do a few things properly. Clear flavours, good balance, and pizzas that don’t need a speech to explain them.",
  },
  {
    icon: Users,
    title: "Made for Real Life",
    text:
      "Beach hair, kids, big groups, quick lunches, long dinners, post-match hunger. Come as you are. We’ll keep the rhythm.",
  },
  {
    icon: Heart,
    title: "Care in the Details",
    text:
      "Nothing is random. Ingredients, timing, music, pace. The best places feel effortless. They aren’t. We just make it look that way.",
  },
];

/* -----------------------------
   ANIMATION VARIANTS
------------------------------ */
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

/* -----------------------------
   1) HERO
------------------------------ */
const AboutHero = () => {
  return (
    <header className="relative w-full min-h-[100svh] overflow-hidden bg-[#004a80] text-white">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="/img/dvibeabout.jpeg"
          alt="Prosika vibe"
          className="w-full h-full object-cover opacity-90"
          loading="eager"
        />
        <div className="absolute inset-0 opacity-[0.07] bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#004a80]/35 via-[#004a80]/15 to-[#004a80]/90 mix-blend-multiply" />
      </div>

      {/* Decorative rings */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
        <div className="absolute top-[18%] left-[6%] w-[300px] h-[300px] md:w-[520px] md:h-[520px] rounded-full border border-white/10" />
        <div className="absolute bottom-[10%] right-[10%] w-[220px] h-[220px] md:w-[420px] md:h-[420px] rounded-full border border-[#FFD700]/15" />
        <div className="absolute top-[14%] right-[15%] w-[110px] h-[110px] md:w-[160px] md:h-[160px] border border-dashed border-white/10 rounded-full" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col min-h-[100svh] pt-20 md:pt-24">
        <div className="flex-1 flex flex-col justify-center items-center px-6">
          <div className="text-center w-full">
            <div className="flex items-center justify-center gap-4 mb-6 md:mb-8">
              <div className="h-[1px] w-8 md:w-16 bg-white/40" />
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-[#FFD700] rounded-full" />
                <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-white/70 uppercase">
                  Pag Island · Prosika Beach · Est. 2022
                </span>
              </div>
              <div className="h-[1px] w-8 md:w-16 bg-white/40" />
            </div>

            <h1 className="font-anton text-[20vw] md:text-[14vw] lg:text-[12vw] leading-[0.85] tracking-[-0.02em] text-white">
              FORZA
            </h1>

            <h1 className="font-anton text-[32vw] md:text-[20vw] lg:text-[16vw] leading-[0.85] tracking-[-0.03em] -mt-[3vw] md:-mt-[2vw]">
              <span
                className="text-transparent"
                style={{ WebkitTextStroke: "2px #60b8ff" }}
              >
                D
              </span>
              <span className="text-[#60b8ff]">10</span>
              <span
                className="text-transparent"
                style={{ WebkitTextStroke: "2px #fc84e6ff" }}
              >
                S
              </span>
            </h1>

            <div className="mt-8 md:mt-12">
              <p className="font-serif italic text-lg md:text-2xl lg:text-3xl text-white/80 max-w-2xl mx-auto leading-relaxed">
                “A beach pizzeria with the{" "}
                <span className="text-[#fc84e6ff]">heartbeat</span> of a stadium.”
              </p>
              <p className="font-manrope text-white/70 max-w-xl mx-auto mt-4 text-sm md:text-base leading-relaxed">
                Built for Prosika nights: salty air, loud tables, and that moment
                when you look up and realise you’ve been here for hours.
              </p>
            </div>

            <div className="mt-10 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#story"
                className="group relative inline-flex items-center gap-3 px-8 py-4 bg-white text-[#004a80] rounded-full overflow-hidden transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] hover:scale-105"
              >
                <span className="relative font-manrope font-bold text-sm tracking-wide">
                  Read Our Story
                </span>
                <ArrowRight
                  size={18}
                  className="relative group-hover:translate-x-1 transition-transform"
                />
              </a>

              <a
                href={D10.menu}
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 font-manrope font-bold text-sm tracking-wide"
              >
                View Menu <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>

        <div className="pb-8 md:pb-12 flex justify-center">
          <a
            href="#story"
            className="flex flex-col items-center gap-2 group cursor-pointer"
          >
            <span className="font-mono text-[10px] tracking-[0.2em] text-white/50 uppercase group-hover:text-white/70 transition-colors">
              Scroll
            </span>
            <div className="w-[1px] h-8 bg-gradient-to-b from-white/50 to-transparent group-hover:from-white/70 transition-colors" />
          </a>
        </div>
      </div>
    </header>
  );
};

/* -----------------------------
   2) STATS BAR
------------------------------ */
const StatsBar = () => {
  return (
    <section className="w-full bg-white border-b border-slate-100 py-10 md:py-16 relative z-30">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12"
        >
          {ABOUT_STATS.map((stat, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              className="flex flex-col items-center justify-center text-center group"
            >
              <div className="mb-3 text-[#0087E6]/30 group-hover:text-[#0087E6] transition-colors duration-300">
                <stat.icon size={28} strokeWidth={1.5} />
              </div>
              <span className="font-anton text-4xl md:text-5xl lg:text-6xl text-[#0087E6] leading-none">
                {stat.value}
              </span>
              <span className="text-[10px] md:text-xs font-manrope font-bold uppercase tracking-[0.2em] text-slate-400 mt-2">
                {stat.label}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* -----------------------------
   3) STORY
------------------------------ */
const TheStory = () => {
  return (
    <section
      id="story"
      className="py-20 md:py-32 bg-white relative overflow-hidden"
    >
      <div className="hidden lg:block absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#F0F9FF] to-transparent z-0" />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative z-10 aspect-[4/5] rounded-[40px] overflow-hidden border-[6px] border-white shadow-2xl max-w-lg mx-auto lg:max-w-none">
              <img
                src="/img/d10vibe2.jpeg"
                alt="D10 vibe"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0087E6]/35 to-transparent" />
            </div>

            <motion.div
              whileHover={{ rotate: 0, scale: 1.05 }}
              className="absolute -bottom-8 md:-bottom-12 right-4 md:-right-8 w-40 h-40 md:w-56 md:h-56 bg-white p-3 shadow-2xl rounded-2xl z-20 rotate-6 transition-transform duration-500 ease-out"
            >
              <img
                src="/img/d10vibe.jpg"
                alt="Prosika moment"
                className="w-full h-full object-cover rounded-xl"
                loading="lazy"
              />
              <div className="absolute -top-3 -left-3 bg-[#FFD700] text-[#0087E6] font-anton text-sm md:text-lg px-3 py-1.5 rounded-lg shadow-lg rotate-[-8deg]">
                PROSIKA NIGHTS
              </div>
            </motion.div>
          </motion.div>

          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="w-10 h-[2px] bg-[#0087E6]" />
              <span className="text-[#0087E6] font-manrope font-bold text-xs uppercase tracking-[0.3em]">
                The Origin Story
              </span>
            </div>

            <h2 className="font-anton text-4xl md:text-5xl lg:text-7xl leading-[0.9] text-[#0087E6] mb-8 uppercase">
              The #10 idea <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0087E6] to-[#004d84]">
                landed on Pag
              </span>
            </h2>

            <div className="space-y-6 font-manrope text-slate-600 text-base md:text-lg leading-relaxed">
              <p>
                In football, the{" "}
                <span className="font-bold text-[#0087E6]">#10</span>{" "}
                makes it look easy. One touch, one pass, and suddenly everything
                opens up. That’s the energy we wanted on Prosika.
              </p>
              <p>
                D10’s is not a “sit quietly” place. It’s a “let the night happen”
                place. People drop in after the beach, after a match, after a long
                drive, after nothing at all. The point is the same: you’re here,
                you’re comfortable, and you’re part of the noise.
              </p>
              <p>
                We care about the details you feel, not the ones you have to
                notice. The pacing, the welcome, the music, the flow of the
                tables. That’s what turns a location into a ritual.
              </p>
            </div>

            <blockquote className="relative border-l-4 border-[#FFD700] pl-6 py-4 my-8 bg-gradient-to-r from-slate-50 to-transparent">
              <Quote
                size={24}
                className="absolute -left-3 -top-2 text-[#FFD700]/30"
              />
              <p className="font-serif italic text-slate-700 text-lg md:text-xl">
                “Not a quiet dinner place. More like a ‘stay a bit longer’ place.”
              </p>
            </blockquote>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href={D10.menu}
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#0087E6] text-white font-bold uppercase text-sm tracking-widest rounded-lg hover:bg-[#006bbd] transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span>See the Menu</span>
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
              <a
                href={D10.googleMaps}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#F0F9FF] text-[#0087E6] font-bold uppercase text-sm tracking-widest rounded-lg hover:bg-white border border-blue-100 transition-all duration-300"
              >
                <MapPin size={16} />
                Find Us
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* -----------------------------
   4) PLAYBOOK
------------------------------ */
const CraftPlaybook = () => {
  return (
    <section className="py-20 md:py-32 bg-[#0087E6] text-white relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-[0.03] bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#0070c0] rounded-full blur-[150px] opacity-50" />
        <div className="absolute bottom-0 right-1/4 w-[420px] h-[420px] bg-[#00a3ff] rounded-full blur-[120px] opacity-30" />
      </div>

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-16 md:mb-20">
          <span className="inline-block py-2 px-5 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm font-mono text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] mb-4">
            The Tactics
          </span>
          <h2 className="font-anton text-4xl md:text-6xl lg:text-7xl uppercase">
            How We Play
          </h2>
          <p className="font-manrope text-white/75 max-w-2xl mx-auto mt-5 text-sm md:text-base leading-relaxed">
            No big speeches. Just the daily stuff that makes a place feel right:
            good energy, simple choices, and doing it properly every day.
          </p>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {PLAYBOOK.map((p, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              whileHover={{ y: -12, scale: 1.02 }}
              className="group bg-white text-slate-900 p-8 md:p-10 rounded-2xl shadow-xl border-b-4 border-transparent hover:border-[#FFD700] transition-all duration-500"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-[#F0F9FF] to-[#d4edff] rounded-2xl flex items-center justify-center mb-6 text-[#0087E6] group-hover:scale-110 transition-transform duration-300">
                <p.icon size={26} strokeWidth={1.5} />
              </div>
              <h3 className="font-anton text-xl md:text-2xl uppercase text-[#0087E6] mb-3">
                {p.title}
              </h3>
              <p className="font-manrope text-sm md:text-base text-slate-600 leading-relaxed">
                {p.text}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* -----------------------------
   5) “WHAT WE BUILD WITH”
------------------------------ */
const IngredientsSection = () => {
  const [active, setActive] = useState(0);
  const activeIngredient = useMemo(() => INGREDIENTS[active], [active]);

  return (
    <section
      id="ingredients"
      className="py-20 md:py-32 bg-[#fafbfc] overflow-hidden"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="text-center mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-3 mb-4"
          >
            <Sparkles size={18} className="text-[#FFD700]" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-slate-500">
              The Starting Lineup
            </span>
            <Sparkles size={18} className="text-[#FFD700]" />
          </motion.div>

          <h2 className="font-anton text-4xl md:text-6xl lg:text-7xl text-[#0087E6] uppercase">
            Pizza, Life, Passion
          </h2>
          <p className="font-serif italic text-slate-500 mt-4 text-base md:text-xl max-w-2xl mx-auto">
            Not a concept. Not a performance. Just a real place with a real
            feeling.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-12 md:mb-16">
          {INGREDIENTS.map((ing, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`relative px-6 py-3 md:px-8 md:py-4 rounded-full font-manrope font-bold text-sm md:text-base transition-all duration-300 ${
                i === active
                  ? "bg-[#0087E6] text-white shadow-lg shadow-[#0087E6]/30"
                  : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
              }`}
            >
              <span
                className={`mr-2 font-anton ${
                  i === active ? "text-white/60" : "text-[#0087E6]"
                }`}
              >
                #{ing.id}
              </span>
              {ing.name}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-5xl mx-auto"
          >
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl border border-slate-100">
              <div className="grid md:grid-cols-2">
                <div className="relative h-[300px] md:h-[520px]">
                  <img
                    src={activeIngredient.img}
                    alt={activeIngredient.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                  <div className="absolute top-6 left-6 w-16 h-16 md:w-20 md:h-20 bg-[#0087E6] rounded-2xl flex items-center justify-center shadow-lg">
                    <span className="font-anton text-2xl md:text-3xl text-white">
                      #{activeIngredient.id}
                    </span>
                  </div>

                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur px-4 py-2.5 rounded-xl text-sm font-bold text-[#0087E6] shadow-lg">
                      <MapPin size={16} className="text-[#FFD700]" />{" "}
                      {activeIngredient.origin}
                    </div>
                  </div>
                </div>

                <div className="p-8 md:p-12 flex flex-col justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FFD700]/10 rounded-full mb-4">
                      <Award size={14} className="text-[#FFD700]" />
                      <span className="text-xs font-bold uppercase tracking-widest text-[#0087E6]">
                        {activeIngredient.position}
                      </span>
                    </div>

                    <h3 className="font-anton text-3xl md:text-4xl lg:text-5xl text-[#0087E6] uppercase leading-[0.9] mb-6">
                      {activeIngredient.name}
                    </h3>

                    <p className="font-manrope text-slate-600 text-base md:text-lg leading-relaxed mb-8">
                      {activeIngredient.story}
                    </p>

                    <div className="bg-gradient-to-r from-[#F0F9FF] to-transparent p-6 rounded-xl border-l-4 border-[#0087E6]">
                      <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                        What it brings
                      </div>
                      <div className="font-serif italic text-xl md:text-2xl text-[#0087E6]">
                        {activeIngredient.signature}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-slate-100">
                    <a
                      href={D10.googleMaps}
                      target="_blank"
                      rel="noreferrer"
                      className="group inline-flex items-center gap-2 text-[#0087E6] font-bold uppercase text-sm tracking-widest hover:gap-4 transition-all duration-300"
                    >
                      Come feel it in person
                      <ArrowRight
                        size={16}
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

/* -----------------------------
   6) TEAM
------------------------------ */
const TeamSection = () => {
  return (
    <section className="py-20 md:py-32 bg-white border-b border-slate-100">
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
          <div>
            <span className="text-[#0087E6] font-bold text-xs uppercase tracking-[0.3em]">
              The Squad
            </span>
            <h2 className="font-anton text-4xl md:text-6xl lg:text-7xl text-[#0087E6] uppercase mt-2">
              Team Sheet
            </h2>
          </div>
          <p className="font-serif italic text-slate-500 text-base md:text-xl md:text-right max-w-sm">
            “Hospitality is a team sport. The best nights happen when everyone’s
            in sync.”
          </p>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {TEAM.map((member, i) => (
            <motion.div key={i} variants={fadeInUp} className="group">
              <div className="relative overflow-hidden rounded-2xl bg-slate-100 aspect-[3/4] mb-6 shadow-lg">
                <img
                  src={member.img}
                  alt={member.name}
                  className="w-full h-full object-cover transition-all duration-700 scale-100 group-hover:scale-105"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-[#0087E6] via-transparent to-transparent opacity-0 group-hover:opacity-90 transition-opacity duration-500" />

                <div className="absolute bottom-0 left-0 w-full p-8 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <div className="text-white font-manrope">
                    <div className="text-xs font-bold uppercase tracking-widest opacity-70 mb-1">
                      Home base
                    </div>
                    <div className="text-xl font-bold">{member.origin}</div>
                  </div>
                </div>
              </div>

              <div className="text-center group-hover:-translate-y-2 transition-transform duration-500">
                <h3 className="font-anton text-2xl uppercase text-[#0087E6]">
                  {member.name}
                </h3>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <ChefHat size={14} className="text-slate-400" />
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
                    {member.role}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* -----------------------------
   7) GUEST STARS
------------------------------ */
const GuestStarsSection = () => {
  return (
    <section className="py-20 md:py-32 bg-white border-b border-slate-100">
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
          <div>
            <span className="text-[#0087E6] font-bold text-xs uppercase tracking-[0.3em]">
              Guest Stars
            </span>
            <h2 className="font-anton text-4xl md:text-6xl lg:text-7xl text-[#0087E6] uppercase mt-2">
              Rotating Lineup
            </h2>
          </div>
          <p className="font-serif italic text-slate-500 text-base md:text-xl md:text-right max-w-sm">
            “Some people are permanent. Some people show up and change the whole
            match.”
          </p>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {GUESTS.map((member, i) => (
            <motion.div key={i} variants={fadeInUp} className="group">
              <div className="relative overflow-hidden rounded-2xl bg-slate-100 aspect-[3/4] mb-6 shadow-lg">
                <img
                  src={member.img}
                  alt={member.name}
                  className="w-full h-full object-cover transition-all duration-700 scale-100 group-hover:scale-105"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-[#0087E6] via-transparent to-transparent opacity-0 group-hover:opacity-90 transition-opacity duration-500" />

                <div className="absolute bottom-0 left-0 w-full p-8 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <div className="text-white font-manrope">
                    <div className="text-xs font-bold uppercase tracking-widest opacity-70 mb-1">
                      From
                    </div>
                    <div className="text-xl font-bold">{member.origin}</div>
                  </div>
                </div>
              </div>

              <div className="text-center group-hover:-translate-y-2 transition-transform duration-500">
                <h3 className="font-anton text-2xl uppercase text-[#0087E6]">
                  {member.name}
                </h3>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <Star size={14} className="text-slate-400" />
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
                    {member.role}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* =========================
   PIZZA "SESSIONS" (with webhook submit)
   ========================= */
const PizzaAcademy = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const WEBHOOK_URL =
    "https://hook.eu2.make.com/cm61co498wsly2lo4tfssadgy2cylu4q";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name?.trim() || !formData.email?.trim()) {
      setError("Please add your name and email.");
      return;
    }

    setSending(true);

    try {
      const payload = {
        source: "d10s-home-private-night",
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: (formData.message || "").trim(),
        page_url: typeof window !== "undefined" ? window.location.href : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        submitted_at: new Date().toISOString(),
      };

      const res = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Request failed (${res.status})`);
      }

      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      setError(
        "Something didn’t send. Please try again, or message us on Instagram."
      );
      console.error("Webhook submit error:", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="academy"
      className="py-16 sm:py-24 bg-white overflow-hidden"
      ref={ref}
    >
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* LEFT: OFFER */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-[#0087E6] font-manrope font-bold text-xs uppercase tracking-widest bg-[#F0F9FF] px-3 py-1.5 rounded-full shadow-sm mb-4">
              Private groups
            </span>

            <h2 className="font-anton text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] uppercase leading-[0.95] mb-4">
              Book a <span className="text-[#0087E6]">Pizza Session</span>
            </h2>

            <p className="font-manrope text-gray-600 leading-relaxed mb-6">
              This is for groups who want D10 to feel like “our place for the
              night”. You pick the reason. We take care of the setup, the flow,
              and the food.
            </p>

            <div className="bg-[#F0F9FF]/50 border border-blue-100 rounded-2xl p-5 sm:p-6 mb-6">
              <h3 className="font-anton text-lg text-[#0F172A] uppercase mb-2">
                Perfect for
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm font-manrope text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  birthdays
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  team dinners
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  friends visiting Pag
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  small celebrations
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  “just because” nights
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  after-wedding dinners
                </li>
              </ul>
            </div>

            <div className="space-y-4 mb-6">
              {[
                {
                  icon: UsersIcon,
                  title: "Your tables reserved",
                  text: "We’ll hold the space for your group so you’re not split around the restaurant.",
                },
                {
                  icon: Utensils,
                  title: "Food plan that fits you",
                  text: "Set menu or share-style “pizza parade”. Easy and fun.",
                },
                {
                  icon: Clock,
                  title: "Timing + flow",
                  text: "Tell us when you want to start. We’ll pace the night properly.",
                },
                {
                  icon: Sparkles,
                  title: "Extras if you want",
                  text: "Cake moment, candles, playlist, little surprises. Optional.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -15 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.2 + i * 0.08 }}
                  className="flex gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-[#F0F9FF] flex items-center justify-center flex-shrink-0 shadow-sm">
                    <item.icon className="w-5 h-5 text-[#0087E6]" />
                  </div>
                  <div>
                    <p className="font-manrope font-bold text-sm text-[#0F172A]">
                      {item.title}
                    </p>
                    <p className="font-manrope text-sm text-gray-600">
                      {item.text}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Link
              to="/academy"
              className="inline-flex items-center gap-2 text-[#0087E6] font-bold uppercase text-sm tracking-wider hover:text-[#0F172A] transition-colors"
            >
              Request a private night
              <ArrowRight size={16} />
            </Link>

            <p className="mt-4 text-xs text-gray-400 font-manrope">
              Minimum planning energy required. Maximum “that was a good night”
              outcome.
            </p>
          </motion.div>

          {/* RIGHT: FORM CARD */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-[#F0F9FF] p-6 sm:p-8 rounded-2xl border border-blue-100 shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#0087E6]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-20 h-20 bg-[#0087E6]/5 rounded-full translate-y-1/2 -translate-x-1/2" />

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#0087E6] flex items-center justify-center">
                    <ChefHat className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-anton text-xl text-[#0F172A] uppercase">
                      Get a plan + availability
                    </h3>
                    <p className="font-manrope text-sm text-gray-500">
                      Send the basics. We’ll reply with dates + options.
                    </p>
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  {!submitted ? (
                    <motion.form
                      key="form"
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onSubmit={handleSubmit}
                      className="space-y-4"
                    >
                      {!!error && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                          <p className="text-sm font-manrope text-red-700">
                            {error}
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          placeholder="Your name"
                          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0087E6] outline-none font-manrope text-base transition-colors bg-white"
                          required
                          disabled={sending}
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          placeholder="you@email.com"
                          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0087E6] outline-none font-manrope text-base transition-colors bg-white"
                          required
                          disabled={sending}
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-2">
                          Details
                        </label>
                        <textarea
                          value={formData.message}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              message: e.target.value,
                            })
                          }
                          placeholder={`Example:\n- Date (or range)\n- Group size\n- Occasion\n- Any “must have”`}
                          rows={4}
                          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0087E6] outline-none font-manrope text-base transition-colors bg-white resize-none"
                          disabled={sending}
                        />
                        <p className="mt-2 text-[11px] text-gray-400 font-manrope">
                          Even “8 people, Friday, birthday” is enough.
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={sending}
                        className={`w-full py-4 text-white font-anton uppercase text-base tracking-widest rounded-xl transition-colors shadow-md flex items-center justify-center gap-2 active:scale-[0.98] ${
                          sending
                            ? "bg-[#0087E6]/60 cursor-not-allowed"
                            : "bg-[#0087E6] hover:bg-[#0F172A]"
                        }`}
                      >
                        <Send size={18} />
                        {sending ? "Sending..." : "Send request"}
                      </button>

                      <p className="text-center text-xs text-gray-400 font-manrope">
                        We reply with availability + a simple plan.
                      </p>
                    </motion.form>
                  ) : (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-10"
                    >
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-green-600" />
                      </div>
                      <h4 className="font-anton text-2xl text-[#0F172A] uppercase mb-2">
                        Request sent.
                      </h4>
                      <p className="font-manrope text-gray-600">
                        We’ll get back to you with dates + options.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                <p className="mt-6 text-xs text-gray-400 font-manrope">
                  PS: If you bring a football team, we accept tactical debates as
                  background music.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* -----------------------------
   PAGE
------------------------------ */
export default function About() {
  return (
    <div className="bg-white min-h-screen w-full overflow-x-hidden selection:bg-[#FFD700] selection:text-[#0087E6]">
      <SEO
        title="About D10’s — Prosika Beach Pizza Spot"
        description="D10’s is a beach pizzeria on Pag. Simple pizza, big energy, and that ‘one more slice’ feeling—right on Prosika."
        image="/og-preview.png"
        canonical="https://www.d10s.pizza/about"
        type="website"
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;600;700;800&family=Playfair+Display:ital,wght@0,400;0,500;1,400;1,500&family=JetBrains+Mono:wght@400;500&display=swap');
        .font-anton { font-family: 'Anton', sans-serif; }
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-serif { font-family: 'Playfair Display', serif; }
        .font-mono { font-family: 'JetBrains Mono', monospace; }

        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        html { scroll-behavior: smooth; -webkit-tap-highlight-color: transparent; }
        body { overflow-x: hidden; }
        button, a { min-height: 44px; }
        @supports (padding: max(0px)) {
          .container {
            padding-left: max(1.5rem, env(safe-area-inset-left));
            padding-right: max(1.5rem, env(safe-area-inset-right));
          }
        }
        * { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
        :focus-visible { outline: 2px solid #0087E6; outline-offset: 2px; }
      `}</style>

      <Navbar />

      <main>
        <AboutHero />
        <StatsBar />
        <TheStory />
        <CraftPlaybook />
        <IngredientsSection />
        <TeamSection />
        <GuestStarsSection />
     
        <PizzaAcademy />
      </main>

      <Footer />
    </div>
  );
}